import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import faqService from "../../../services/faqService";
import { instanceToken } from "../../../utils/constant";
import {
  EditPageLayout,
  EditFieldLabel,
} from "../../../components/edit/EditPageLayout";

const categories = {
  types: "အမျိုးအစားများ",
  basic: "အခြေခံ",
  other: "အခြား",
};

export default function CreateFaq({ homeAlert }) {
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [questionMM, setQuestionMM] = useState("");
  const [answers, setAnswers] = useState([{ answer_en: "", answer_mm: "" }]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("");
  const [categoryMM, setCategoryMM] = useState("");
  const [errors, setErrors] = useState({});

  const handleAddAnswer = () => {
    setAnswers([...answers, { answer_en: "", answer_mm: "" }]);
  };

  const handleAnswerChange = (index, field, value) => {
    const updatedAnswers = [...answers];
    updatedAnswers[index][field] = value;
    setAnswers(updatedAnswers);
  };

  const handleRemoveAnswer = (index) => {
    const updatedAnswers = [...answers];
    updatedAnswers.splice(index, 1);
    setAnswers(updatedAnswers);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const err = {};

    if (!question.trim()) err.question = "Question in English is required.";
    if (!questionMM.trim()) err.questionMM = "Question in Myanmar is required.";
    if (!category) err.category = "Category is required.";
    if (
      answers.some(
        (answer) => !answer.answer_en.trim() || !answer.answer_mm.trim()
      )
    ) {
      err.answers = "Both English and Myanmar answers are required.";
    }

    if (Object.keys(err).length > 0) {
      setErrors(err);
      return;
    }

    try {
      setLoading(true);
      await faqService.createFaq(instanceToken.token, {
        question_mm: questionMM,
        question_en: question,
        category_en: category,
        category_mm: categoryMM,
        answers,
      });
      homeAlert("FAQ has been created.", false);
      navigate("/faqs");
    } catch (error) {
      homeAlert("Failed to create FAQ!", true);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <EditPageLayout
      title="Create FAQ"
      subtitle="Add a new frequently asked question."
      breadcrumbs={[
        { label: "FAQs", path: "/faqs" },
        { label: "Create" },
      ]}
      listPath="/faqs"
      loading={loading}
      submitLabel="Create"
      onSubmit={handleCreate}
    >
      <div className="edit-form-row-2">
        <FormControl>
          <EditFieldLabel required>Category (English)</EditFieldLabel>
          <Select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setCategoryMM(categories[e.target.value] || "");
              setErrors({});
            }}
            error={!!errors.category}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select Category
            </MenuItem>
            <MenuItem value="types">Types</MenuItem>
            <MenuItem value="basic">Basic</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
          {errors.category && (
            <FormHelperText error>{errors.category}</FormHelperText>
          )}
        </FormControl>

        <FormControl>
          <EditFieldLabel>Category (Myanmar)</EditFieldLabel>
          <Select
            value={categoryMM}
            onChange={(e) => {
              const selectedCategory = Object.keys(categories).find(
                (key) => categories[key] === e.target.value
              );
              setCategory(selectedCategory || "");
              setCategoryMM(e.target.value);
              setErrors({});
            }}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select Category
            </MenuItem>
            {Object.values(categories).map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className="edit-form-row-2">
        <div>
          <EditFieldLabel required>Question (English)</EditFieldLabel>
          <TextField
            fullWidth
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            error={!!errors.question}
            helperText={errors.question}
          />
        </div>
        <div>
          <EditFieldLabel required>Question (Myanmar)</EditFieldLabel>
          <TextField
            fullWidth
            value={questionMM}
            onChange={(e) => setQuestionMM(e.target.value)}
            error={!!errors.questionMM}
            helperText={errors.questionMM}
          />
        </div>
      </div>

      <div className="edit-form-answers">
        <div className="edit-form-answers-head">
          <h3 className="edit-form-answers-title">Answers</h3>
          <button
            type="button"
            className="edit-btn edit-btn-back"
            onClick={handleAddAnswer}
          >
            + Add Answer
          </button>
        </div>
        {errors.answers && (
          <p className="edit-field-error">{errors.answers}</p>
        )}
        {answers.map((answer, index) => (
          <div key={index} className="edit-form-answer-row">
            <div>
              <EditFieldLabel required>Answer EN {index + 1}</EditFieldLabel>
              <TextField
                fullWidth
                value={answer.answer_en}
                onChange={(e) =>
                  handleAnswerChange(index, "answer_en", e.target.value)
                }
                error={!!errors.answers && !answer.answer_en.trim()}
              />
            </div>
            <div>
              <EditFieldLabel required>Answer MM {index + 1}</EditFieldLabel>
              <TextField
                fullWidth
                value={answer.answer_mm}
                onChange={(e) =>
                  handleAnswerChange(index, "answer_mm", e.target.value)
                }
                error={!!errors.answers && !answer.answer_mm.trim()}
              />
            </div>
            {answers.length > 1 && (
              <button
                type="button"
                className="edit-btn edit-btn-remove-outline"
                onClick={() => handleRemoveAnswer(index)}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </EditPageLayout>
  );
}
